import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import { TableBody, TableCell, TableRow, Tooltip } from '@mui/material';
import { Claims, useNotification, useOidc } from '@revenuewell/front-end-bundle';
import { SocialMediaSettings } from 'apps/wrapper/src/services/social-media/types/social-media-settings';
import { FC, useEffect, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { Review } from '../../../../../../services/patient-feedback-service/patient-feedback-service-types';
import { DirectCommunicationType } from '../../../../../../services/practice-portal/direct-communication/types';
import { DirectMailTemplateEditorDialog } from '../../../../template/template-editor/direct-mail-template-editor-dialog';
import { SocialMediaType } from '../../../types/social-media-type';
import { ComposeSocialMediaPost } from '../../compose-social-media-post/compose-social-media-post';
import { ConnectFacebookDialog } from '../../connect-facebook-dialog/connect-facebook-dialog';
import { FacebookButton, GoogleButton, Hr, InfoTitle, Table } from '../../dialog.styles';

interface PostToButtonsProps {
  review: Review | null;
  socialMediaSettings: SocialMediaSettings | null;
  onPostSend?: () => Promise<void>;
}

export const PostToButtons: FC<PostToButtonsProps> = (props) => {
  const { review, onPostSend, socialMediaSettings } = props;
  const { showSuccess } = useNotification();
  const { getClaims } = useOidc();
  const { contactDetails, contactLocation } = usePatientProfile();
  const [loggedUser, setLoggedUser] = useState<Claims | null>(null);
  const [selectedSocialMediaType, setSelectedSocialMediaType] = useState<SocialMediaType | null>(null);
  const [templateEditorOpened, setTemplateEditorOpened] = useState(false);
  const [connectFacebookOpen, setConnectFacebookOpen] = useState(false);
  const [connectedPageName, setConnectedPageName] = useState<string | null>(
    socialMediaSettings?.platformAvailability?.facebookPageName ?? null
  );
  const [locallyConnected, setLocallyConnected] = useState<boolean>(
    !!socialMediaSettings?.platformAvailability?.facebookConnected
  );

  useEffect(() => {
    getClaims().then((claims) => {
      setLoggedUser(claims);
    });
  }, []);

  useEffect(() => {
    setConnectedPageName(socialMediaSettings?.platformAvailability?.facebookPageName ?? null);
    setLocallyConnected(!!socialMediaSettings?.platformAvailability?.facebookConnected);
  }, [socialMediaSettings?.platformAvailability?.facebookPageName, socialMediaSettings?.platformAvailability?.facebookConnected]);

  const handleReviewRequestSent = async () => {
    showSuccess('Review Request has been sent');
    onPostSend && onPostSend();
  };

  const handleFacebookClick = () => {
    if (!socialMediaSettings || !review) return;
    if (locallyConnected) {
      setSelectedSocialMediaType('Facebook');
    } else {
      setConnectFacebookOpen(true);
    }
  };

  const handleFacebookConnected = (pageName: string) => {
    setConnectedPageName(pageName);
    setLocallyConnected(true);
    setConnectFacebookOpen(false);
    // Auto-open compose immediately after connecting
    setSelectedSocialMediaType('Facebook');
  };

  if (Number(loggedUser?.locationId) !== review?.practiceId) return null;

  return (
    <>
      <Hr />
      <Table className="table-info" style={{ marginBottom: 0 }}>
        <TableBody>
          <TableRow>
            <TableCell style={{ width: '15%' }}>
              <InfoTitle>Post to:</InfoTitle>
            </TableCell>
            <TableCell>
              <Tooltip
                title={
                  locallyConnected
                    ? null
                    : 'Click to connect Facebook account'
                }
              >
                <FacebookButton
                  startIcon={<FacebookIcon />}
                  onClick={handleFacebookClick}
                  className={locallyConnected ? '' : 'inactive'}
                >
                  Facebook
                </FacebookButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {contactDetails?.email?.toLowerCase()?.includes('@gmail.com') && (
        <>
          <Hr />
          <Table className="table-info" style={{ marginBottom: 0 }}>
            <TableBody>
              <TableRow>
                <TableCell style={{ width: '32%' }}>
                  <InfoTitle>Ask Patient to Post to:</InfoTitle>
                </TableCell>
                <TableCell>
                  {contactDetails?.email?.toLowerCase()?.includes('@gmail.com') && (
                    <GoogleButton startIcon={<GoogleIcon />} onClick={() => setTemplateEditorOpened(true)}>
                      Google
                    </GoogleButton>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      )}

      {contactDetails && contactDetails.location && review && (
        <DirectMailTemplateEditorDialog
          open={templateEditorOpened}
          onClose={() => setTemplateEditorOpened(false)}
          onSend={handleReviewRequestSent}
          patientId={contactDetails.id}
          locationId={contactDetails?.location?.id}
          reviewId={review?.id}
          directCommunicationType={DirectCommunicationType.PostReviewToGoogle}
          title="Send Review Request"
          subject={`Thank you, ${contactDetails?.firstName}! Can you help us post the review to Google?`}
          fromName={contactLocation?.name}
          buttons={{ submit: { title: 'Send' } }}
        />
      )}

      {connectFacebookOpen && contactDetails?.location && (
        <ConnectFacebookDialog
          open={connectFacebookOpen}
          locationId={contactDetails.location.id}
          facebookAppId={socialMediaSettings?.platformAvailability?.facebookAppId}
          facebookPermissionScopes={socialMediaSettings?.platformAvailability?.facebookPermissionScopes}
          onClose={() => setConnectFacebookOpen(false)}
          onConnected={handleFacebookConnected}
        />
      )}

      {selectedSocialMediaType && (
        <ComposeSocialMediaPost
          open={selectedSocialMediaType != null}
          review={review}
          socialMediaType={selectedSocialMediaType}
          onClose={() => setSelectedSocialMediaType(null)}
          onPostSend={onPostSend}
        />
      )}
    </>
  );
};

